/**
 * @file EulaModal.jsx
 * @feature EULA
 * @description Pure presentational modal. All state lives in useEula.
 * Receives callbacks; knows nothing about MSAL or UserContext.
 */

import React from 'react';

const EULA_SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    body: `By accessing or using our platform, you acknowledge that you have read, understood,
and agree to be bound by these Terms of Service. If you do not agree with any part
of these terms, you may not use our services.`,
  },
  {
    title: '2. Privacy Policy',
    body: `Your privacy is important to us. Our Privacy Policy explains how we collect, use,
and protect your personal information. By using our services, you consent to our
data practices as described in our Privacy Policy.`,
  },
  {
    title: '3. User Accounts',
    body: `When you create an account, you must provide accurate and complete information.
You are responsible for maintaining the security of your account credentials and
for all activities that occur under your account.`,
  },
  {
    title: '4. Acceptable Use',
    body: `You agree not to use our services for any unlawful purpose or in any way that
could damage, disable, overburden, or impair our services.`,
  },
  {
    title: '5. Intellectual Property',
    body: `All content and materials available through our services are protected by
intellectual property rights. You may not use, reproduce, or distribute this
content without explicit permission.`,
  },
  {
    title: '6. Limitation of Liability',
    body: `To the maximum extent permitted by law, we shall not be liable for any indirect,
incidental, special, consequential, or punitive damages resulting from your use
of, or inability to use, our services.`,
  },
  {
    title: '7. Modification of Terms',
    body: `We reserve the right to modify these terms at any time. Continued use of our
services after any such changes constitutes your acceptance of the new terms.`,
  },
  {
    title: '8. Termination',
    body: `We reserve the right to terminate or suspend your account at our sole discretion,
without notice, for conduct that violates these Terms.`,
  },
  {
    title: '9. Governing Law',
    body: `These Terms shall be governed by the laws of the jurisdiction in which the company
operates, without regard to its conflict-of-law provisions.`,
  },
  {
    title: '10. Contact Information',
    body: `If you have any questions about these Terms, please contact us through the support
channels provided in the application.`,
  },
];

/**
 * @param {Object}   props
 * @param {boolean}  props.isChecked
 * @param {boolean}  props.hasScrolled
 * @param {Function} props.onScroll
 * @param {Function} props.onToggleCheck
 * @param {Function} props.onAccept
 */
const EulaModal = ({ isChecked, hasScrolled, onScroll, onToggleCheck, onAccept }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-lg bg-opacity-60"
    style={{ backdropFilter: 'blur(4px)' }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="eula-title"
  >
    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 py-4 px-6 flex justify-between items-center">
        <h2 id="eula-title" className="text-xl font-bold text-gray-800">
          Terms of Service &amp; Privacy Policy
        </h2>
        <span className="text-xs text-gray-500">Last updated: February 20, 2025</span>
      </div>

      {/* Scrollable Body */}
      <div
        className="px-6 py-4 h-72 overflow-y-auto text-gray-600 text-sm"
        onScroll={onScroll}
      >
        <h3 className="font-semibold text-lg mb-2 text-gray-800">Welcome to Our Platform</h3>
        <p className="mb-4">
          By accessing or using our services, you agree to be bound by these Terms and Conditions.
          Please take the time to read them carefully.
        </p>

        {EULA_SECTIONS.map(({ title, body }) => (
          <React.Fragment key={title}>
            <h4 className="font-semibold text-md mb-2 text-gray-800">{title}</h4>
            <p className="mb-4">{body}</p>
          </React.Fragment>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="agree-terms"
            checked={isChecked}
            onChange={onToggleCheck}
            disabled={!hasScrolled}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="agree-terms" className="ml-2 block text-sm text-gray-700">
            I have read and agree to the Terms of Service and Privacy Policy
            {!hasScrolled && (
              <span className="text-red-500 ml-1">(please scroll to the end to enable)</span>
            )}
          </label>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onAccept}
            disabled={!isChecked}
            className={`px-4 py-2 rounded-md text-white font-medium transition-colors ${
              isChecked
                ? 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Accept &amp; Continue
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default EulaModal;